/* eslint-disable @typescript-eslint/no-unused-vars */
import { AddIcon } from '@chakra-ui/icons'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Stack,
  useDisclosure,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useReportingForMonth from '../../hooks/useReportingForMonth'
import {
  AmountUnit,
  DurationUnit,
  FrequencyUnit,
  Method,
  Percentage,
  Regimen,
  SelectedFields,
} from '../../types/Regimen'
import useRegimens from '../../utils/useRegimens'
import DeleteConfirmDialog from './DeleteConfirmDialog'
import EndRegimenModal from './EndRegimenModal'
import RegimenFormModal from './RegimenFormModal'
import RegimensTable from './RegimensTable'
import useCurrentReport from '../../hooks/useCurrentReport'
import useUpdateReportDoc from '../../hooks/useUpdateReportDoc'
import ShowCompletedSwitch from './ShowCompletedSwitch'
import { RecordLabel } from './types'

interface RegimenListItem {
  label: string
  fieldId: string
  items: { [itemId: string]: string }
  id: string
}

interface RegimensProps {
  lists: RegimenListItem[]
  amountEnabled?: boolean
  frequencyEnabled?: boolean
  durationEnabled?: boolean
  recordLabel: RecordLabel
  methodEnabled?: boolean
  percentageEnabled?: boolean
  allowUpdate?: boolean
  alert?: string
}

export default function Regimens({
  lists,
  amountEnabled,
  frequencyEnabled,
  durationEnabled,
  recordLabel,
  methodEnabled,
  percentageEnabled,
  allowUpdate = true,
  alert,
}: RegimensProps) {
  // Modal and dialog states
  const modal = useDisclosure()
  const [confirmDelete, setConfirmDelete] = useState<{
    recordId: string
    usageIndex: number
  }>()
  const [confirmEndId, setConfirmEndId] = useState<string>()
  const deleteCancelRef = useRef<HTMLButtonElement>(null)

  // Form state

  const [selectedFields, setSelectedFields] = useState<SelectedFields>({})
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [frequency, setFrequency] = useState('')
  const [amount, setAmount] = useState('')
  const [duration, setDuration] = useState('')
  const [method, setMethod] = useState<Method>()
  const [frequencyUnit, setFrequencyUnit] = useState<FrequencyUnit>()
  const [amountUnit, setAmountUnit] = useState<AmountUnit>()
  const [durationUnit, setDurationUnit] = useState<DurationUnit>()
  const [percentage, setPercentage] = useState<Percentage>()
  const [editingId, setEditingId] = useState<string>()
  const [editingUsageIndex, setEditingUsageIndex] = useState<number>()
  const [showCompleted, setShowCompleted] = useState(false)

  // Data loading
  const currentReport = useCurrentReport()
  const updateReport = useUpdateReportDoc()
  const records = useRegimens(lists[0].id)
  const location = useLocation()
  const { month, year } = useReportingForMonth()

  // Derived state
  const reportingFor = location.pathname.split('/')[2]
  const reportingForStart = reportingFor + '-01'
  const reportingForEnd = reportingFor + '-31'

  let supplementListIsAccurate = false
  let medicationListIsAccurate = false
  let physicalStrategyListIsAccurate = false
  let dentalListIsAccurate = false
  if (currentReport) {
    supplementListIsAccurate = currentReport.supplementListIsAccurate ?? false
    medicationListIsAccurate = currentReport.medicationListIsAccurate ?? false
    physicalStrategyListIsAccurate =
      currentReport.physicalStrategyListIsAccurate ?? false
    dentalListIsAccurate = currentReport.dentalListIsAccurate ?? false
  }

  // Handlers
  const closeConfirmDelete = () => setConfirmDelete(undefined)
  const closeConfirmEnd = () => setConfirmEndId(undefined)

  const clear = () => {
    setStartDate('')
    setEndDate('')
    setSelectedFields({})
    setFrequency('')
    setFrequencyUnit(undefined)
    setAmount('')
    setDuration('')
    setMethod(undefined)
    setAmountUnit(undefined)
    setDurationUnit(undefined)
    setEditingId(undefined)
    setEditingUsageIndex(undefined)
    setPercentage(undefined)
  }

  // Custom modal.onClose to ensure form state is reset
  const modalOnClose = modal.onClose
  modal.onClose = () => {
    clear()
    setReactivatingPrimaryId(undefined)
    modalOnClose()
  }

  const canReactivate = true

  // Reactivate state — opens the existing Add modal pre-filled from the latest usage
  const [reactivatingPrimaryId, setReactivatingPrimaryId] = useState<
    string | undefined
  >()

  const handleReactivate = (recordId: string) => {
    const record = records.find((r) => r.id === recordId)
    if (!record) return
    const siblings = records
      .filter((r) => r.primaryId === record.primaryId)
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
    const latestRegimen = siblings[siblings.length - 1]
    const latestUsage = latestRegimen?.usages[latestRegimen.usages.length - 1]
    if (!latestUsage) return
    setSelectedFields(latestUsage.fields)
    setStartDate('')
    setAmount(latestUsage.amount?.toString() ?? '')
    setAmountUnit(latestUsage.amountUnit)
    setFrequency(latestUsage.frequency?.toString() ?? '')
    setFrequencyUnit(latestUsage.frequencyUnit)
    setMethod(latestUsage.method)
    setReactivatingPrimaryId(record.primaryId)
    modal.onOpen()
  }

  return (
    <>
      <Stack spacing={4}>
        {!!alert && <Alert status='info'>{alert}</Alert>}
        {recordLabel === 'Supplement' && (
          <Checkbox
            mt={2}
            mb={2}
            isChecked={supplementListIsAccurate}
            onChange={(e) =>
              updateReport({ supplementListIsAccurate: e.target.checked })
            }
          >
            The supplement list below is currently accurate.
          </Checkbox>
        )}
        {recordLabel === 'Medication' && (
          <Checkbox
            mt={2}
            mb={2}
            isChecked={medicationListIsAccurate}
            onChange={(e) =>
              updateReport({ medicationListIsAccurate: e.target.checked })
            }
          >
            The medication list below is currently accurate.
          </Checkbox>
        )}
        {recordLabel === 'Physical Strategy' && (
          <Checkbox
            mt={2}
            mb={2}
            isChecked={physicalStrategyListIsAccurate}
            onChange={(e) =>
              updateReport({ physicalStrategyListIsAccurate: e.target.checked })
            }
          >
            The physical strategy list below is currently accurate.
          </Checkbox>
        )}
        {recordLabel === 'Dental' && (
          <Checkbox
            mt={2}
            mb={2}
            isChecked={dentalListIsAccurate}
            onChange={(e) =>
              updateReport({ dentalListIsAccurate: e.target.checked })
            }
          >
            The dental list below is currently accurate.
          </Checkbox>
        )}
        <Box>
          <Button
            rightIcon={<AddIcon />}
            onClick={() => {
              modal.onOpen()
            }}
            aria-label={`Add ${recordLabel}`}
          >
            Add {recordLabel}
          </Button>
        </Box>
        <ShowCompletedSwitch
          recordLabel={recordLabel}
          showCompleted={showCompleted}
          onChange={setShowCompleted}
        />

        <RegimensTable
          lists={lists}
          records={records}
          amountEnabled={amountEnabled}
          frequencyEnabled={frequencyEnabled}
          durationEnabled={durationEnabled}
          methodEnabled={methodEnabled}
          percentageEnabled={percentageEnabled}
          allowUpdate={allowUpdate}
          canReactivate={canReactivate}
          recordLabel={recordLabel}
          reportingForStart={reportingForStart}
          month={month}
          year={year}
          showCompleted={showCompleted}
          setEditingId={setEditingId}
          setEditingUsageIndex={setEditingUsageIndex}
          setStartDate={setStartDate}
          setFrequency={setFrequency}
          setFrequencyUnit={setFrequencyUnit}
          setAmount={setAmount}
          setAmountUnit={setAmountUnit}
          setMethod={setMethod}
          setSelectedFields={setSelectedFields}
          setDuration={setDuration}
          setDurationUnit={setDurationUnit}
          setPercentage={setPercentage}
          modal={modal}
          setConfirmEndId={setConfirmEndId}
          setConfirmDelete={setConfirmDelete}
          onReactivate={handleReactivate}
        />
      </Stack>

      <RegimenFormModal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        lists={lists}
        editingId={editingId}
        editingUsageIndex={editingUsageIndex}
        recordLabel={recordLabel}
        records={records}
        reportingForEnd={reportingForEnd}
        amountEnabled={amountEnabled}
        frequencyEnabled={frequencyEnabled}
        durationEnabled={durationEnabled}
        methodEnabled={methodEnabled}
        percentageEnabled={percentageEnabled}
        initialStartDate={startDate}
        initialFrequency={frequency}
        initialFrequencyUnit={frequencyUnit}
        initialAmount={amount}
        initialAmountUnit={amountUnit}
        initialMethod={method}
        initialSelectedFields={selectedFields}
        initialDuration={duration}
        initialDurationUnit={durationUnit}
        initialPercentage={percentage}
        reactivatingPrimaryId={reactivatingPrimaryId}
      />

      <DeleteConfirmDialog
        isOpen={!!confirmDelete}
        onClose={closeConfirmDelete}
        confirmDelete={confirmDelete}
        cancelRef={deleteCancelRef}
        records={records}
        recordLabel={recordLabel}
      />

      <EndRegimenModal
        isOpen={!!confirmEndId}
        onClose={closeConfirmEnd}
        confirmEndId={confirmEndId}
        records={records}
        recordLabel={recordLabel}
      />
    </>
  )
}
